/**
 * Users Page
 *
 * Main page for user management (admin only).
 *
 * Spec 277: read-only. FCP does not create users, change roles or deactivate users (FabricControlMgmt
 * owns user identity); only email notification preferences stay editable here. The earlier FR-005/FR-066
 * add/modify/remove and role-change controls were removed by Spec 277.
 *
 * Conformed to Spec 197 design blueprint — migrated off the bespoke
 * header / p-6 container / hand-rolled stat cards / raw filters / UserList
 * (DualScrollTable) / UserForm (DetailModal) onto the canonical Tailwind+token
 * UI kit. Presentation only — no data, logic, state, route, or behavior change.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ShieldAlert } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { apiClient, ApiError } from '../services/api';
import { systemHelp } from '../../../data/help/systemHelp';
import { EmailKillSwitch } from '../../../components/admin/EmailKillSwitch';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Toggle,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Drawer,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';
import {
  fetchNotificationStats,
  fetchPreferenceSummaries,
  fetchUserPreferences,
  updateUserPreference,
} from '@/services/notificationApi';
import type {
  NotificationStats,
  NotificationPreferencesResponse,
  UserPreferenceSummary,
} from '@shared/types/notifications';

// Types - matches camelCase from backend API
export interface User {
  id: string;
  azureOid: string;
  email: string;
  displayName: string;
  role: 'admin' | 'manager' | 'operator' | 'viewer';
  isActive: boolean;
  preferenceSummary?: UserPreferenceSummary;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserFilters {
  role?: string | undefined;
  isActive?: boolean | undefined;
  search?: string | undefined;
}

export interface UserStatistics {
  total: number;
  active: number;
  inactive: number;
  byRole: {
    admin: number;
    manager: number;
    operator: number;
    viewer: number;
  };
}

// ============================================================================
// Helpers (presentation only)
// ============================================================================

const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'Full access',
  manager: 'Manage & monitor',
  operator: 'Read & execute',
  viewer: 'Read only',
};

function getInitials(displayName: string | undefined, email: string): string {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  }
  return email.charAt(0).toUpperCase();
}

function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return formatEasternDate(date);
}


// ============================================================================
// Page
// ============================================================================

/** One empty list, so memos keyed on the rows stay stable before the first load. */
const NO_USERS: User[] = [];

export const Users: React.FC = () => {
  const { canAccess } = useAuth();
  // The preference drawer's errors. A failed list load or refresh is the list query's own error.
  const [drawerError, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // Filters
  const [filters, setFilters] = useState<UserFilters>({});

  // Modal state
  // 🛑 Spec 277 FR-006 — user create/edit form state removed (no user mutation in FCP).

  // Spec 253: email-stats header + per-user preference summaries + drill-down drawer
  const [emailStats, setEmailStats] = useState<NotificationStats | null>(null);
  const [prefSummaries, setPrefSummaries] = useState<Record<string, UserPreferenceSummary>>({});
  const [emailStateFilter, setEmailStateFilter] = useState<'all' | 'receiving' | 'none'>('all');
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  const [drawerPrefs, setDrawerPrefs] = useState<NotificationPreferencesResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch users. Spec 226 (FR-011): React Query keeps the rows of the current filters, sort and page on
  // screen through a refresh, and a failed refresh shows the Refresh badge.
  const usersQuery = useQuery({
    queryKey: ['users', 'list', filters, page, sortBy, sortOrder],
    queryFn: async () => {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit,
        sortBy,
        sortOrder,
      };

      if (filters.role) params.role = filters.role;
      if (filters.isActive !== undefined) params.isActive = filters.isActive;
      if (filters.search) params.search = filters.search;

      return apiClient.get<{
        items: User[];
        totalPages: number;
        total: number;
      }>('/api/v1/users', params);
    },
  });
  const statisticsQuery = useQuery({
    queryKey: ['users', 'statistics'],
    queryFn: () => apiClient.get<UserStatistics>('/api/v1/users/statistics'),
  });
  const users = usersQuery.data?.items ?? NO_USERS;
  const totalPages = usersQuery.data?.totalPages || 1;
  const total = usersQuery.data?.total || 0;
  const statistics = statisticsQuery.data ?? null;
  const loading = usersQuery.isLoading;
  const listError = usersQuery.data || !usersQuery.error
    ? null
    : usersQuery.error instanceof ApiError
      ? `Failed to load users: ${usersQuery.error.message}`
      : usersQuery.error instanceof Error ? usersQuery.error.message : 'Failed to load users';
  const error = drawerError ?? listError;

  // Spec 253: email stats + per-user preference summaries (stable callback)
  const refreshEmailData = useCallback(async () => {
    try {
      const [stats, summaries] = await Promise.all([fetchNotificationStats('7d'), fetchPreferenceSummaries()]);
      setEmailStats(stats);
      const map: Record<string, UserPreferenceSummary> = {};
      for (const s of summaries) map[s.userId] = s.summary;
      setPrefSummaries(map);
    } catch (err) {
      console.error('Failed to load email preference data:', err);
    }
  }, []);

  useEffect(() => {
    refreshEmailData();
  }, [refreshEmailData]);

  // The header's standard Refresh: the list, the tiles and the email figures.
  const { refetch: refetchUsers } = usersQuery;
  const { refetch: refetchStatistics } = statisticsQuery;
  const refreshAll = useCallback(async () => {
    setError(null);
    await Promise.all([
      refetchUsers({ throwOnError: true }),
      refetchStatistics({ throwOnError: true }),
      refreshEmailData(),
    ]);
  }, [refetchUsers, refetchStatistics, refreshEmailData]);

  // Check access
  if (!canAccess('manage:users')) {
    return (
      <PageLayout title="User Management" description="Manage user access and role assignments.">
        <Card>
          <EmptyState
            icon={<ShieldAlert className="h-10 w-10" />}
            title="Access Denied"
            message="You do not have permission to access user management."
          />
        </Card>
      </PageLayout>
    );
  }

  // Handle filter changes
  const handleFilterChange = (newFilters: UserFilters): void => {
    setFilters(newFilters);
    setPage(1);
  };

  // Spec 253: open the per-user preference drawer + edit on behalf (admin)
  const openPreferenceDrawer = async (user: User): Promise<void> => {
    setDrawerUser(user);
    setDrawerPrefs(null);
    setDrawerLoading(true);
    try {
      setDrawerPrefs(await fetchUserPreferences(user.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleDrawerToggle = async (categoryKey: string, targetKey: string, optedIn: boolean): Promise<void> => {
    if (!drawerUser) return;
    setDrawerSaving(true);
    try {
      await updateUserPreference(drawerUser.id, { categoryKey, targetKey, optedIn });
      setDrawerPrefs(await fetchUserPreferences(drawerUser.id));
      void refreshEmailData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update preference');
    } finally {
      setDrawerSaving(false);
    }
  };

  // Handle sort changes
  const handleSortChange = (field: string): void => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // 🛑 Spec 277 FR-006 — user create/edit/role/deactivate/reactivate handlers removed (dead: all UI triggers gone).
  // Sortable header helper for DataTable column headers
  const sortHeader = (field: string, label: string): React.ReactNode => (
    <button
      type="button"
      onClick={() => handleSortChange(field)}
      className="flex items-center gap-1 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
    >
      {label}
      {sortBy === field && (
        <span className="text-[var(--color-text-secondary)]">{sortOrder === 'asc' ? '↑' : '↓'}</span>
      )}
    </button>
  );

  const columns: DataTableColumn<User>[] = [
    {
      key: 'displayName',
      header: sortHeader('displayName', 'User'),
      width: '28%',
      render: (user) => (
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-sm font-medium text-white">
            {getInitials(user.displayName, user.email)}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-[var(--color-text-primary)]">
              {user.displayName || 'Unknown'}
            </div>
            <div className="truncate text-[var(--color-text-secondary)]">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: sortHeader('role', 'Role'),
      width: '14%',
      // 🛑 Spec 277 FR-006 — role is READ-ONLY in FCP; user identity/lifecycle (incl. role) is
      //    managed by FabricControlMgmt. Displayed as static text, no role-change control.
      render: (user) => {
        const role = (user.role as string).toLowerCase();
        return (
          <AppTooltip title={ROLE_DESCRIPTIONS[role] ?? ''}>
            <span className="capitalize text-[var(--color-text-primary)]">{role}</span>
          </AppTooltip>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '10%',
      render: (user) =>
        user.isActive ? (
          <Badge variant="success" isDot>
            Active
          </Badge>
        ) : (
          <Badge variant="secondary" isDot>
            Inactive
          </Badge>
        ),
    },
    {
      key: 'lastLoginAt',
      header: sortHeader('lastLoginAt', 'Last Login'),
      width: '11%',
      render: (user) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatRelativeTime(user.lastLoginAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: sortHeader('createdAt', 'Created'),
      width: '11%',
      render: (user) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatRelativeTime(user.createdAt)}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      header: sortHeader('updatedAt', 'Updated'),
      width: '11%',
      render: (user) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatRelativeTime(user.updatedAt)}
        </span>
      ),
    },
    {
      key: 'emailPrefs',
      header: 'Email Prefs',
      width: '13%',
      render: (user) => {
        const s = prefSummaries[user.id];
        if (!s) return <span className="text-[var(--color-text-secondary)]">—</span>;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {s.anyEmail ? (
              <Badge variant="success" size="xs">
                On
              </Badge>
            ) : (
              <Badge variant="secondary" size="xs">
                Off
              </Badge>
            )}
            {s.escalationOptedOutRules > 0 && (
              <Badge variant="warning" size="xs">
                −{s.escalationOptedOutRules} rules
              </Badge>
            )}
            {s.pipelineOptIns > 0 && (
              <Badge variant="info" size="xs">
                {s.pipelineOptIns} pipe
              </Badge>
            )}
            {s.kpiOptIns > 0 && (
              <Badge variant="info" size="xs">
                {s.kpiOptIns} kpi
              </Badge>
            )}
          </div>
        );
      },
    },
    // 🛑 Spec 277 FR-006 — the "Actions" column (edit / deactivate / reactivate) is REMOVED.
    //    FCP does not manage user identity/lifecycle; those endpoints were unmounted. Read-only.
  ];

  return (
    <PageLayout
      title="User Management"
      description="View users and their access. User identity and roles are managed in FabricControlMgmt."
      onRefresh={refreshAll}
      isRefreshing={usersQuery.isFetching || statisticsQuery.isFetching}
      lastFetchedAt={usersQuery.dataUpdatedAt}
      refreshError={usersQuery.error ?? statisticsQuery.error}
      actions={
        <>
          <PageHelpPanel content={systemHelp} buttonSize="sm" />
          {/* 🛑 Spec 277 FR-006 — no "Add User" control: FCP does not create users
              (user identity/lifecycle is managed in FabricControlMgmt). */}
        </>
      }
    >
      {/* Spec 255: emergency email kill switch — stop/resume ALL notification email in one click */}
      <EmailKillSwitch />

      {/* Statistics tiles */}
      {statistics && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
          <StatCard label="Total Users" value={statistics.total} />
          <StatCard
            label="Active"
            value={statistics.active}
            valueClassName="text-[var(--color-status-success)]"
          />
          <StatCard
            label="Inactive"
            value={statistics.inactive}
            valueClassName="text-[var(--color-text-secondary)]"
          />
          <AppTooltip title="Users with full administrative privileges">
            <StatCard
              label="Admins"
              value={statistics.byRole.admin}
              valueClassName="text-[var(--color-accent)]"
            />
          </AppTooltip>
          <StatCard label="Managers" value={statistics.byRole.manager} />
          <StatCard label="Operators" value={statistics.byRole.operator} />
          <StatCard label="Viewers" value={statistics.byRole.viewer} />
        </div>
      )}

      {/* Spec 253: email notification statistics (last 7 days) */}
      {emailStats && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Emails sent (7d)" value={emailStats.sent} />
          <StatCard
            label="Failed (7d)"
            value={emailStats.failed}
            valueClassName={emailStats.failed > 0 ? 'text-[var(--color-status-danger)]' : undefined}
          />
          <StatCard
            label="Suppressed (7d)"
            value={emailStats.suppressed}
            valueClassName="text-[var(--color-text-secondary)]"
          />
          <AppTooltip title="Active users with at least one email category enabled">
            <StatCard
              label="Receiving email"
              value={emailStats.usersReceiving}
              valueClassName="text-[var(--color-status-success)]"
            />
          </AppTooltip>
          <StatCard
            label="Not receiving"
            value={emailStats.usersNotReceiving}
            valueClassName="text-[var(--color-text-secondary)]"
          />
          <AppTooltip
            title={`Transport: ${emailStats.transportInUse}. ${
              emailStats.globalEmailEnabled ? 'Email delivery is enabled.' : 'Email delivery is currently disabled.'
            }`}
          >
            <StatCard
              label="Delivery"
              value={emailStats.globalEmailEnabled ? 'On' : 'Off'}
              subtext={emailStats.transportInUse}
              valueClassName={
                emailStats.globalEmailEnabled
                  ? 'text-[var(--color-status-success)]'
                  : 'text-[var(--color-text-secondary)]'
              }
            />
          </AppTooltip>
        </div>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
          <div className="md:col-span-2">
            <Select
              isFullWidth
              label="Role"
              value={filters.role || ''}
              onChange={(e) => handleFilterChange({ ...filters, role: e.target.value || undefined })}
              options={[
                { value: '', label: 'All Roles' },
                { value: 'admin', label: 'Admin' },
                { value: 'operator', label: 'Operator' },
                { value: 'viewer', label: 'Viewer' },
              ]}
            />
          </div>
          <div className="md:col-span-2">
            <Select
              isFullWidth
              label="Status"
              value={filters.isActive === undefined ? '' : filters.isActive.toString()}
              onChange={(e) =>
                handleFilterChange({
                  ...filters,
                  isActive: e.target.value === '' ? undefined : e.target.value === 'true',
                })
              }
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
            />
          </div>
          <div className="md:col-span-2">
            <Select
              isFullWidth
              label="Email"
              value={emailStateFilter}
              onChange={(e) => setEmailStateFilter(e.target.value as 'all' | 'receiving' | 'none')}
              options={[
                { value: 'all', label: 'All Email' },
                { value: 'receiving', label: 'Receiving' },
                { value: 'none', label: 'Not receiving' },
              ]}
            />
          </div>
          <div className="md:col-span-4">
            <label className="mb-1.5 block text-sm font-medium text-[var(--color-text-primary)]">
              Search
            </label>
            <AppTooltip title="Search users by email address or display name">
              <Input
                isFullWidth
                type="text"
                placeholder="Search by email or name..."
                value={filters.search || ''}
                onChange={(e) =>
                  handleFilterChange({ ...filters, search: e.target.value || undefined })
                }
                leftAddon={<Search className="h-4 w-4" />}
              />
            </AppTooltip>
          </div>
          <div className="md:col-span-2">
            <Button variant="ghost" isFullWidth onClick={() => handleFilterChange({})}>
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-6">
          {/* "Try again" reloads the list. It used to only clear the message, which then showed "No users found". */}
          <ErrorState title="Error" message={error} onRetry={() => { setError(null); return refreshAll(); }} />
        </div>
      )}

      {/* User table */}
      <Card padding="none">
        {loading ? (
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            title="No users found"
            message="No users match your current filters. Try adjusting the filters."
          />
        ) : (
          <DataTable
            columns={columns}
            rows={
              emailStateFilter === 'all'
                ? users
                : users.filter((u) => {
                    const s = prefSummaries[u.id];
                    const receiving = s ? s.anyEmail : true;
                    return emailStateFilter === 'receiving' ? receiving : !receiving;
                  })
            }
            rowKey={(user) => user.id}
            pageSize={limit}
            /* This page owns pagination (server-side, with its own page
               controls), so the table must not render a second one. The blueprint's
               DataTable rules name this case: "Without it you get two paginators
               stacked." */
            showPagination={false}
            minWidth="52rem"
            emptyMessage="No users found"
            onRowClick={(user) => openPreferenceDrawer(user)}
          />
        )}
      </Card>

      {/* Spec 253: per-user email preference drawer (admin manage-on-behalf) */}
      <Drawer
        isOpen={drawerUser !== null}
        onClose={() => setDrawerUser(null)}
        title={
          drawerUser ? `Email preferences — ${drawerUser.displayName || drawerUser.email}` : 'Email preferences'
        }
        width="lg"
      >
        {drawerLoading || !drawerPrefs ? (
          <LoadingState rows={6} />
        ) : (
          <div className="space-y-5">
            {drawerPrefs.categories.map((cat) => (
              <div key={cat.categoryKey}>
                <div className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">{cat.displayName}</div>
                <ul className="divide-y divide-[var(--color-border)]">
                  {cat.items.map((item) => (
                    <li key={item.targetKey} className="flex items-center justify-between gap-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm text-[var(--color-text-primary)]">{item.label}</span>
                        {!item.isExplicit && (
                          <Badge variant="secondary" size="xs">
                            default
                          </Badge>
                        )}
                      </div>
                      <Toggle
                        checked={item.effective}
                        disabled={drawerSaving}
                        onChange={(next) => handleDrawerToggle(cat.categoryKey, item.targetKey, next)}
                        aria-label={`${item.effective ? 'Disable' : 'Enable'} ${item.label}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      {/* Server-side pagination (preserved) */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-[var(--color-text-secondary)]">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} users
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      </Extended>

      {/* 🛑 Spec 277 FR-006 — user create/edit modal removed; FCP is read-only on user identity. */}
    </PageLayout>
  );
};

export default Users;
